import React, { useState, useEffect, useCallback } from 'react';
import { Settings as SettingsIcon, Save, Server } from 'lucide-react';
import api from '../../utils/api';
import { parseDate } from '../../utils/dates';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import './Settings.css';

function Settings() {
  const { user } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [branding, setBranding] = useState({
    app_name: '',
    login_prompt: '',
    login_footer_note: '',
  });

  const [systemInfo, setSystemInfo] = useState(null);

  const formatUptime = (seconds) => {
    if (!seconds && seconds !== 0) return '--';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);
    return parts.join(' ');
  };

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const [brandingRes, sysRes, balanceRes] = await Promise.all([
        api.get('/admin/branding'),
        api.get('/admin/system-info').catch(() => null),
        api.get(`/users/${user.id}/balance`).catch(() => null),
      ]);
      setBranding({
        app_name: brandingRes.branding?.app_name || '',
        login_prompt: brandingRes.branding?.login_prompt || '',
        login_footer_note: brandingRes.branding?.login_footer_note || '',
      });
      if (sysRes) {
        setSystemInfo(sysRes);
      }
      if (balanceRes?.pto_year) {
        setSystemInfo(prev => ({ ...prev, pto_year: balanceRes.pto_year }));
      }
    } catch (err) {
      // Don't loop -- use defaults
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleBrandingChange = (field, value) => {
    setBranding(prev => ({ ...prev, [field]: value }));
  };

  const handleBrandingSave = async () => {
    setSaving(true);
    try {
      await api.patch('/admin/branding', branding);
      toast.success('Branding settings saved');
    } catch (err) {
      toast.error('Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-settings">
        <div className="page-header">
          <SettingsIcon size={22} strokeWidth={2} />
          <h1>Settings</h1>
        </div>
        <div className="settings-loading">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="admin-settings">
      <div className="page-header">
        <SettingsIcon size={22} strokeWidth={2} />
        <h1>Settings</h1>
      </div>

      {/* Branding Section */}
      <div className="settings-section">
        <h2 className="settings-section-title">Branding</h2>
        <p className="settings-section-desc">Customize the appearance of FPS OS for your team.</p>

        <div className="settings-form">
          <div className="settings-field">
            <label>Application Name</label>
            <input
              type="text"
              value={branding.app_name}
              onChange={(e) => handleBrandingChange('app_name', e.target.value)}
              placeholder="FPS OS"
            />
            <span className="field-hint">Displayed in the sidebar and browser tab</span>
          </div>

          <div className="settings-field">
            <label>Login Page Message</label>
            <textarea
              value={branding.login_prompt}
              onChange={(e) => handleBrandingChange('login_prompt', e.target.value)}
              placeholder="Sign in with your Front Porch Solutions Google account"
              rows={2}
            />
            <span className="field-hint">Shown on the login page above the sign-in button</span>
          </div>

          <div className="settings-field">
            <label>Login Footer Note</label>
            <input
              type="text"
              value={branding.login_footer_note}
              onChange={(e) => handleBrandingChange('login_footer_note', e.target.value)}
              placeholder="Only @frontporchsolutions.com accounts can sign in"
            />
            <span className="field-hint">Shown below the sign-in button on the login page</span>
          </div>

          <div className="settings-actions">
            <button className="btn-primary" onClick={handleBrandingSave} disabled={saving}>
              <Save size={14} />
              {saving ? 'Saving...' : 'Save Branding'}
            </button>
          </div>
        </div>
      </div>

      {/* System Information Section */}
      <div className="settings-section settings-section--system">
        <div className="settings-section-header-row">
          <Server size={15} strokeWidth={2} className="system-info-icon" />
          <h2 className="settings-section-title">System Information</h2>
        </div>
        <div className="settings-info-grid">
          <div className="settings-info-item">
            <span className="info-label">Version</span>
            <span className="info-value">{systemInfo?.version || '--'}</span>
          </div>
          <div className="settings-info-item">
            <span className="info-label">Build</span>
            <span className="info-value info-value--mono">{systemInfo?.build || '--'}</span>
          </div>
          <div className="settings-info-item">
            <span className="info-label">Environment</span>
            <span className="info-value">{systemInfo?.environment || '--'}</span>
          </div>
          <div className="settings-info-item">
            <span className="info-label">Node</span>
            <span className="info-value info-value--mono">{systemInfo?.node_version || '--'}</span>
          </div>
          <div className="settings-info-item">
            <span className="info-label">Uptime</span>
            <span className="info-value">{formatUptime(systemInfo?.uptime_seconds)}</span>
          </div>
          <div className="settings-info-item">
            <span className="info-label">PTO Year</span>
            <span className="info-value">{systemInfo?.pto_year?.label || '--'} ({systemInfo?.pto_year ? `${parseDate(systemInfo.pto_year.start_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${parseDate(systemInfo.pto_year.end_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''})</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
